import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { format, addDays } from 'date-fns';
import { Phone, MessageCircle, Mail, MessageSquare, Users, Linkedin, ListTodo, Loader2 } from 'lucide-react';
import FileUpload from './FileUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TASK_TYPES = [
    { value: 'Call', icon: Phone, color: 'text-blue-600' },
    { value: 'Email', icon: Mail, color: 'text-purple-600' },
    { value: 'WhatsApp', icon: MessageCircle, color: 'text-green-600' },
    { value: 'SMS', icon: MessageSquare, color: 'text-orange-600' },
    { value: 'Meeting', icon: Users, color: 'text-red-600' },
    { value: 'LinkedIn', icon: Linkedin, color: 'text-blue-700' },
    { value: 'Todo', icon: ListTodo, color: 'text-slate-600' },
];

const QUICK_PRESETS = [
    { label: 'Tomorrow', days: 1 },
    { label: '3 Days', days: 3 },
    { label: '1 Week', days: 7 },
    { label: '2 Weeks', days: 14 },
    { label: '1 Month', days: 30 },
];

const ScheduleFollowUpModal = ({ open, onClose, leadId, leadName, onSaved }) => {
    const { user } = useAuth();
    const [teamMembers, setTeamMembers] = useState([]);
    const [saving, setSaving] = useState(false);
    const taskFileUploadRef = useRef(null);

    const [taskType, setTaskType] = useState('Call');
    const [dueDate, setDueDate] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (open) {
            fetchTeamMembers();
            setTaskType('Call');
            setDueDate('');
            setAssigneeId(String(user?.id || ''));
            setDescription('');
        }
    }, [open, user]);

    const fetchTeamMembers = async () => {
        try {
            const res = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(res.data);
        } catch (e) {
            console.error('Failed to fetch team members', e);
        }
    };

    const applyPreset = (days) => {
        setDueDate(format(addDays(new Date(), days), 'yyyy-MM-dd'));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!dueDate) {
            toast.error('Please set a due date');
            return;
        }

        try {
            setSaving(true);
            const [year, month, day] = dueDate.split('-').map(Number);
            const due = new Date(year, month - 1, day, 23, 59, 59, 999);

            const taskRes = await axios.post(`${API_URL}/tasks`, {
                lead_id: leadId,
                description: description.trim() || `Follow up with ${leadName} via ${taskType}`,
                type: taskType,
                due_date: due.toISOString(),
                ...(assigneeId ? { assignee_id: assigneeId } : {}),
            });
            const taskId = taskRes.data.id;
            let uploadFailed = false;
            if (taskFileUploadRef.current?.hasPending()) {
                try {
                    await taskFileUploadRef.current.uploadPending(leadId, 'task', taskId);
                } catch (uploadErr) {
                    console.error('File upload failed after scheduling follow-up', uploadErr);
                    uploadFailed = true;
                }
            }

            if (uploadFailed) {
                toast.warning('Task created but attachments failed to upload. Open the Files tab to retry.');
            } else {
                toast.success('Follow-up scheduled');
            }
            onSaved && onSaved();
            onClose && onClose();
        } catch (err) {
            toast.error(getApiError(err, 'Failed to schedule follow-up'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Schedule Follow-up — {leadName}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-5 pt-1">
                    {/* Task type */}
                    <div className="space-y-2">
                        <Label>Type</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {TASK_TYPES.map(t => {
                                const Icon = t.icon;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => setTaskType(t.value)}
                                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-all ${
                                            taskType === t.value
                                                ? 'border-slate-900 bg-slate-900 text-white'
                                                : 'border-slate-200 hover:border-slate-400 text-slate-600'
                                        }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {t.value}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick presets */}
                    <div className="space-y-2">
                        <Label>Due date <span className="text-red-500">*</span></Label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {QUICK_PRESETS.map(p => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => applyPreset(p.days)}
                                    className={`px-2.5 py-1 text-xs rounded-md border font-medium transition-all ${
                                        dueDate === format(addDays(new Date(), p.days), 'yyyy-MM-dd')
                                            ? 'border-slate-900 bg-slate-900 text-white'
                                            : 'border-slate-200 hover:bg-slate-100 text-slate-600'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <Input
                            type="date"
                            value={dueDate}
                            onChange={e => setDueDate(e.target.value)}
                            min={format(new Date(), 'yyyy-MM-dd')}
                            className="text-sm"
                        />
                    </div>

                    {/* Assignee */}
                    <div className="space-y-1">
                        <Label>Assign to</Label>
                        <Select value={assigneeId} onValueChange={setAssigneeId}>
                            <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Me (default)" />
                            </SelectTrigger>
                            <SelectContent>
                                {teamMembers.map(m => (
                                    <SelectItem key={m.id} value={String(m.id)}>{m.email}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                        <Label>Description</Label>
                        <Input
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={`Follow up with ${leadName || 'lead'} via ${taskType}`}
                            className="text-sm"
                        />
                    </div>

                    {/* Attachments */}
                    <div className="space-y-1">
                        <Label>Attachments</Label>
                        <FileUpload
                            ref={taskFileUploadRef}
                            leadId={leadId}
                            entityType="task"
                            uploadAfterCreate={true}
                            compact={true}
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Schedule Follow-up
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ScheduleFollowUpModal;
